import mapiConfig from "../configs/mapi";
import { logError } from "./utils/logService";

import * as Sentry from "@sentry/browser";
import { companyModel } from "../models/hubspotModel";
import { httpMStepsClient } from "./utils/httpService";
import { sortCollection } from "../utils/sortCollection";
import { HubspotDispatcher } from "../redux/dispatcher/hubspotDispatcher";
import { actionTypesHubspot } from "../redux/constants/actionTypesHubspot";

const BASE_URL = mapiConfig.api.domain;
const Controller = "/hubspot";

export const getAllHubspotCompaniesList = async (
  tableHeaders: Record<string, Record<string, string | number | boolean>>
): Promise<void> => {
  const methodPath = `${Controller}/companies?direction=DESCENDING&limit=100`;
  let companiesResult: Array<companyModel> = [];

  await httpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      const transformedResults = result.data.results.map(
        ({ properties, ...rest }: any) => ({
          ...rest,
          ...properties,
        })
      );
      companiesResult = sortCollection(
        tableHeaders,
        "sortDirection",
        transformedResults
      );
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });

  HubspotDispatcher(actionTypesHubspot.ListOfCompanies, companiesResult);
};

export const getAllFilteredHubspotCompaniesList = async (
  tableHeaders: Record<string, Record<string, string | number | boolean>>,
  filters: { status?: string; dateRange?: { from: string; to: string } }
): Promise<void> => {
  HubspotDispatcher(actionTypesHubspot.IsCompaniesTableRefreshed, false);

  const methodPath = `${Controller}/companies?direction=DESCENDING&limit=100`;

  // Generate query parameters
  const queryParams = Object.entries(filters)
    .flatMap(([key, value]) =>
      key === "dateRange"
        ? [
            value?.from ? `from=${encodeURIComponent(value.from)}` : "",
            value?.to ? `to=${encodeURIComponent(value.to)}` : "",
          ].filter(Boolean) // Remove empty strings
        : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

  // Append query parameters correctly (use `&` instead of `?` if methodPath already has a query)
  const fullPath = queryParams ? `${methodPath}&${queryParams}` : methodPath;

  let operationResult: Array<companyModel> = [];

  await httpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: fullPath,
  })
    .then((result) => {
      const transformedResults = result.data.results.map(
        ({ properties, ...rest }: any) => ({
          ...rest,
          ...properties,
        })
      );
      operationResult = sortCollection(
        tableHeaders,
        "sortDirection",
        transformedResults
      );
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });

  HubspotDispatcher(actionTypesHubspot.FilterCompanies, operationResult);
};

export const getSalesSearchOperations = async (
  tableHeaders: Record<string, Record<string, string | number | boolean>>,
  filters?: { status?: string; dateRange?: { from: string; to: string } },
  search?: string
): Promise<void> => {
  const methodPath = `${Controller}/companies?direction=DESCENDING&limit=100`;

  // Generate query parameters
  const queryParams = [
    // Handle filters
    ...(filters
      ? Object.entries(filters).flatMap(([key, value]) =>
          key === "dateRange" && value
            ? [
                value.from ? `from=${encodeURIComponent(value.from)}` : "",
                value.to ? `to=${encodeURIComponent(value.to)}` : "",
              ].filter(Boolean) // Remove empty values
            : value != null
            ? [
                `${encodeURIComponent(key)}=${encodeURIComponent(
                  String(value)
                )}`,
              ]
            : []
        )
      : []),

    // Handle search
    ...(search ? [`name=${encodeURIComponent(search)}`] : []),
  ]
    .filter(Boolean) // Remove empty query parameters
    .join("&");

  // Ensure query string is added correctly
  const fullPath = queryParams ? `${methodPath}&${queryParams}` : methodPath;

  let operationResult: Array<companyModel> = [];

  await httpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: fullPath,
  })
    .then((result) => {
      const transformedResults = result.data.results.map(
        ({ properties, ...rest }: any) => ({
          ...rest,
          ...properties,
        })
      );
      operationResult = sortCollection(
        tableHeaders,
        "sortDirection",
        transformedResults
      );
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
    });

  HubspotDispatcher(actionTypesHubspot.SearchCompanies, operationResult);
};

export const addHubspotCompany = async (requestBody: any) => {
  const methodPath = `${Controller}/companies`;

  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "POST",
    path: methodPath,
    body: requestBody,
  })
    .then((result) => {
      return {
        data: result.data,
        isSuccessfull: true,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        isSuccessfull: false,
        isLoading: false,
        data: error,
      };
    });
};

export const getHubspotCompanyDetailsById = async (companyId: string) => {
  const methodPath = `${Controller}/companies/${companyId}`;

  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "GET",
    path: methodPath,
  })
    .then((result) => {
      return result.data;
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);
      return error;
    });
};

export const deleteHubspotCompanyById = async (companyId: string) => {
  const methodPath = `${Controller}/companies/${companyId}`;
  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "DELETE",
    path: methodPath,
  })
    .then((result) => {
      return {
        data: result.data,
        isSuccessfull: true,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        isSuccessfull: false,
        isLoading: false,
      };
    });
};

export const updateHubspotCompany = async (
  requestBody: any,
  companyId: string
) => {
  const methodPath = `${Controller}/companies/${companyId}`;

  return await httpMStepsClient({
    baseURL: BASE_URL,
    type: "PUT",
    path: methodPath,
    body: requestBody,
  })
    .then((result) => {
      return {
        data: result.data,
        isSuccessfull: true,
        isLoading: false,
      };
    })
    .catch((error) => {
      logError(error);
      Sentry.captureException(error);

      return {
        isSuccessfull: false,
        isLoading: false,
        data: error,
      };
    });
};
